import React, { memo, useMemo } from 'react';
import { Badge, Icon, Link, Menus, Text, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import { CAMPAIGNS_MARKETING } from 'constants/Entities';

import { getDateFromBackendISO } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';

import {
    getMarketingCampaignStatusBadgeType,
    getMarketingCampaignStatusLabel,
    MARKETING_CAMPAIGN_STATUS,
    normalizeMarketingCampaignStatusId,
} from '../../../utils/marketingCampaignStatus';

import './styles.scss';

function formatCount(value) {
    const n = Number(value);
    const t = Number.isFinite(n) ? Math.trunc(n) : 0;
    return t.toLocaleString();
}

function sharePct(part, total) {
    const p = Number(part);
    const t = Number(total);
    if (!Number.isFinite(t) || t <= 0) return null;
    const pn = Number.isFinite(p) ? Math.trunc(p) : 0;
    return `${((pn / t) * 100).toFixed(2)}%`;
}

/** Prefer launch → schedule → updated → created for “sent on” semantics. */
function pickSentOnDateBlock({ launchAt, scheduleAt, updatedAt, createdAt }) {
    if (launchAt?.time) return launchAt;
    if (scheduleAt?.time) return scheduleAt;
    if (updatedAt?.time) return updatedAt;
    return createdAt;
}

function sentOnSubtitle({ launchAt, scheduleAt, updatedAt, createdAt, userCreated, userUpdated }) {
    const dateBlock = pickSentOnDateBlock({ launchAt, scheduleAt, updatedAt, createdAt });
    const time = dateBlock?.time;
    const userName = userUpdated?.label ?? userCreated?.label ?? null;

    if (!time) {
        return userName ?? null;
    }

    const datePart = getDateFromBackendISO(time, 'LL');
    const timePart = getDateFromBackendISO(time, 'LT');
    const main = getLiteralWithParameters('label_sent_on_at', [datePart, timePart]);

    if (!userName) {
        return main;
    }

    return `${main}. ${userName}`;
}

function statusPresentation(status) {
    const id = normalizeMarketingCampaignStatusId(status);
    return {
        badgeType: getMarketingCampaignStatusBadgeType(id),
        label: getMarketingCampaignStatusLabel(id),
    };
}

function MarketingCampaignRow({
    data = {},
    onPreviewAndTest,
    onDuplicateCampaign,
    onDeleteCampaign,
    colDef,
}) {
    const theme = useTheme();
    const {
        id: campaignId,
        name,
        status,
        totalRecipients,
        totalReaders,
        totalClickers,
        totalCanceled,
        launchAt,
        scheduleAt,
        createdAt,
        updatedAt,
        userCreated,
        userUpdated,
    } = data;

    const subtitle = sentOnSubtitle({
        launchAt,
        scheduleAt,
        updatedAt,
        createdAt,
        userCreated,
        userUpdated,
    });
    const { badgeType, label: statusLabel } = statusPresentation(status);

    const metrics = [
        {
            labelKey: 'label_recipients',
            value: formatCount(totalRecipients),
            rate: null,
            wide: false,
        },
        {
            labelKey: 'label_marketing_campaigns_metrics_opens',
            value: formatCount(totalReaders),
            rate: sharePct(totalReaders, totalRecipients),
            wide: false,
        },
        {
            labelKey: 'label_marketing_campaigns_metrics_clicks',
            value: formatCount(totalClickers),
            rate: sharePct(totalClickers, totalRecipients),
            wide: false,
        },
        {
            labelKey: 'label_marketing_campaigns_metrics_unsubscribed',
            value: formatCount(totalCanceled),
            rate: sharePct(totalCanceled, totalRecipients),
            wide: true,
        },
    ];

    const openPreviewAndTest = onPreviewAndTest ?? colDef?.cellRendererParams?.onPreviewAndTest;
    const openDuplicate = onDuplicateCampaign ?? colDef?.cellRendererParams?.onDuplicateCampaign;
    const openDelete = onDeleteCampaign ?? colDef?.cellRendererParams?.onDeleteCampaign;

    const statusId = normalizeMarketingCampaignStatusId(status);
    const showDeleteInMenu =
        statusId === MARKETING_CAMPAIGN_STATUS.DRAFT ||
        statusId === MARKETING_CAMPAIGN_STATUS.SCHEDULED;

    const menuItems = useMemo(() => {
        if (!campaignId) return [];
        const path = `${CAMPAIGNS_MARKETING.route}/edit/${campaignId}`;
        const goToEdit = () => ensureRoute(path);
        const items = [
            {
                title: <Text>{getLiteral('action_edit')}</Text>,
                icon: <Icon name="edit" />,
                onClick: goToEdit,
            },
            {
                title: <Text>{getLiteral('label_marketing_campaigns_preview_and_test')}</Text>,
                icon: <Icon name="visibilityOn" />,
                onClick: () => openPreviewAndTest?.(campaignId),
            },
            {
                title: <Text>{getLiteral('action_duplicate')}</Text>,
                icon: <Icon name="duplicate" />,
                onClick: () => openDuplicate?.(campaignId),
            },
        ];
        if (showDeleteInMenu) {
            items.push(
                { divider: true },
                {
                    title: (
                        <Text color={theme.colors.textLight.negative}>
                            {getLiteral('action_delete')}
                        </Text>
                    ),
                    icon: <Icon name="delete" color={theme.colors.semantic.negative500} />,
                    onClick: () => openDelete?.(campaignId),
                },
            );
        }
        return items;
    }, [
        campaignId,
        openDelete,
        openDuplicate,
        openPreviewAndTest,
        showDeleteInMenu,
        theme.colors.textLight.negative,
        theme.colors.semantic.negative500,
    ]);

    const editPath = `${CAMPAIGNS_MARKETING.route}/edit/${campaignId}`;

    return (
        <div className="fm-marketing-campaign-row">
            <div className="fm-marketing-campaign-row__info">
                <div className="fm-marketing-campaign-row__title-wrap">
                    <Link href={`#${editPath}`}>
                        <Text
                            className="fm-marketing-campaign-row__title"
                            type="subtitle"
                            medium
                            isTruncated
                            useTooltip
                        >
                            {name ?? ''}
                        </Text>
                    </Link>
                </div>
                {subtitle && (
                    <Text
                        className="fm-marketing-campaign-row__subtitle"
                        color={theme.colors.utility.textSecondary}
                        type="caption"
                        isTruncated
                        useTooltip
                    >
                        {subtitle}
                    </Text>
                )}
            </div>
            <div className="fm-marketing-campaign-row__status-column">
                <Badge
                    className="fm-marketing-campaign-row__status"
                    isTruncated
                    useTooltip
                    type={badgeType}
                >
                    {statusLabel}
                </Badge>
            </div>
            <div className="fm-marketing-campaign-row__metrics">
                {metrics.map(({ labelKey, value, rate, wide }) => (
                    <div
                        key={labelKey}
                        className={classnames('fm-marketing-campaign-row__metric', {
                            'fm-marketing-campaign-row__metric--wide': wide,
                        })}
                    >
                        <Text
                            className="fm-marketing-campaign-row__metric-label"
                            color={theme.colors.utility.textSecondary}
                            type="caption"
                        >
                            {getLiteral(labelKey)}
                        </Text>
                        <Text
                            className="fm-marketing-campaign-row__metric-value"
                            type="subtitle"
                            medium
                        >
                            {value}
                        </Text>
                        {rate != null ? (
                            <Text
                                className="fm-marketing-campaign-row__metric-rate"
                                color={theme.colors.utility.textSecondary}
                                type="badges"
                            >
                                {rate}
                            </Text>
                        ) : null}
                    </div>
                ))}
            </div>
            {campaignId ? (
                <div className="fm-marketing-campaign-row__actions">
                    <Tooltip content={getLiteral('action_edit')} placement="top">
                        <span
                            className="fm-marketing-campaign-row__action-btn"
                            role="button"
                            tabIndex={0}
                            onClick={() => ensureRoute(editPath)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    ensureRoute(editPath);
                                }
                            }}
                        >
                            <Icon name="edit" size="large" color={theme.colors.actionMinor[500]} />
                        </span>
                    </Tooltip>
                    <Menus
                        classes={{ trigger: 'fm-marketing-campaign-row__action-btn' }}
                        icon={<Icon name="threeDots" color={theme.colors.actionMinor[500]} />}
                        items={menuItems}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default memo(MarketingCampaignRow);
